import { useCallback, useEffect, useRef, useState } from 'react';
import { FaFilePdf } from 'react-icons/fa6';
import { FaTriangleExclamation } from 'react-icons/fa6';
import { FaArrowRotateRight } from 'react-icons/fa6';
import { Document, Page, pdfjs } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';

import { usePdfFields } from './hooks/usePdfFields';
import { usePdfZoom } from './hooks/usePdfZoom';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// Import required CSS

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
// Import custom CSS for PDF annotation override
import './pdf-annotation-override.css';
import { ProgressBar } from '../ProgressBar/ProgressBar';

// PDF viewer options
const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
};

type PDFFile = string | File | null;

function PdfError({ onRetry }: { onRetry?: () => void } = {}) {
  return (
    <div className="bg-white w-full h-full grid place-items-center">
      <div className="flex flex-col items-center gap-2 text-text-error">
        <FaTriangleExclamation className="size-6" />
        <p>Error loading your PDF</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 text-brand-breeze-primary-500 flex gap-2 items-center hover:text-brand-breeze-primary-600"
            title="Retry loading PDF"
          >
            <FaArrowRotateRight className="size-5" />
            <div className="text-larger">Try again</div>
          </button>
        )}
      </div>
    </div>
  );
}

export function PdfLoading() {
  return (
    <div className="relative bg-white grid w-full h-full place-items-center">
      <div className="flex flex-col items-center gap-4">
        <ProgressBar className="min-h-1 w-64" />
        <div className="text-brand-breeze-primary-500 font-semibold">
          PDF Certificate loading...
        </div>
      </div>
    </div>
  );
}

const ZoomControls = ({
  zoomScale,
  zoomIn,
  zoomOut,
}: {
  zoomScale: number;
  zoomIn: () => void;
  zoomOut: () => void;
}) => (
  <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-md z-10">
    <button
      onClick={zoomOut}
      disabled={zoomScale <= 0.5}
      className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
      title="Zoom out"
    >
      -
    </button>
    <span className="text-sm">{Math.round(zoomScale * 100)}%</span>
    <button
      onClick={zoomIn}
      disabled={zoomScale >= 3}
      className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
      title="Zoom in"
    >
      +
    </button>
  </div>
);

export function PdfViewer({
  file,
  initialScale = 1.0,
  allowZoom = true,
  onChange,
  onRetry,
  onInitialize,
}: {
  file?: PDFFile;
  initialScale?: number;
  onChange?: (fieldState: Record<string, string>) => void;
  onInitialize?: (fieldState: Record<string, string>) => void;
  onRetry?: () => void;
  allowZoom?: boolean;
}) {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const { zoomScale, zoomIn, zoomOut } = usePdfZoom(initialScale);

  const { initializeFieldsFromPdf } = usePdfFields({
    containerRef: containerRef.current,
    onChange,
    onInitialize,
  });

  const onDocumentLoadSuccess = useCallback(
    async (pdf: PDFDocumentProxy): Promise<void> => {
      setLoading(false);
      setError(null);
      setNumPages(pdf.numPages);
      initializeFieldsFromPdf(pdf);
    },
    [initializeFieldsFromPdf],
  );

  const onDocumentLoadError = useCallback((error: Error): void => {
    console.error('[ERROR] Failed to load PDF:', error);
    setLoading(false);
    setError('Failed to load PDF document');
  }, []);

  useEffect(() => {
    if (file) {
      setLoading(true);
      setError(null);
    } else {
      setLoading(false);
    }
  }, [file]);

  return (
    <div className="relative h-full flex items-center justify-center overflow-hidden  bg-gray-700 px-3">
      <div
        className="items-center justify-items-start overflow-auto h-full w-full"
        ref={containerRef}
      >
        {loading && <PdfLoading />}

        {error && <PdfError onRetry={onRetry} />}

        {!file && !loading && !error && (
          <div className="flex flex-col items-center justify-center p-6 text-gray-500 bg-white absolute inset-0 ">
            <FaFilePdf className="size-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium mb-2">No PDF document loaded</p>
            <p className="text-sm text-gray-400">
              Please provide a PDF file to view
            </p>
          </div>
        )}

        {file && (
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={''}
            error={''}
            options={options}
            className="flex flex-col items-center gap-4 justify-items-start"
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                className={'mb-4'}
                renderForms={true}
                scale={zoomScale}
                loading={
                  <div className="flex justify-center">
                    <div className="size-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                }
                width={
                  containerRef.current
                    ? containerRef.current.clientWidth * zoomScale
                    : undefined
                }
              />
            ))}
          </Document>
        )}
      </div>

      {file && !loading && !error && allowZoom && (
        <ZoomControls zoomScale={zoomScale} zoomIn={zoomIn} zoomOut={zoomOut} />
      )}
    </div>
  );
}
